/**
 * Widget App
 *
 * Minimal React application used when the calculator runs as an embedded widget.
 * It renders only the DynamicFlowEngine (the questionnaire) wrapped with the
 * providers it needs (QueryClient, ErrorBoundary, WidgetConfig).
 *
 * Unlike the standalone App.tsx, this component:
 *   - Has no React Router (the widget does not manipulate the host's URL)
 *   - Uses its own QueryClient instance (isolates cache from any host React app)
 *   - Dispatches a CustomEvent on the document when the flow completes,
 *     so the host page can listen without tight coupling
 */

import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from '@posthog/react';
import { createQueryClient } from '../app/queryClient';
import DynamicFlowEngine from '../components/flow/DynamicFlowEngine';
import ErrorBoundary from '../components/ErrorBoundary';
import { getPostHogClient } from '../shared/posthog';
import { WidgetConfig } from './types';
import { WidgetConfigProvider } from './WidgetConfigContext';

const posthogClient = getPostHogClient();

interface WidgetAppProps {
  config: WidgetConfig;
}

const widgetQueryClient = createQueryClient();

const WidgetApp: React.FC<WidgetAppProps> = ({ config }) => {
  const country = config.country || null;
  const taxYear = config.taxYear || 2024;
  const region = config.region || undefined;

  const handleComplete = (summary: unknown) => {
    const event = new CustomEvent('tax-calculator:complete', {
      detail: summary,
      bubbles: true,
    });
    document.dispatchEvent(event);

    if (config.onComplete) {
      config.onComplete(summary);
    }
  };

  const content = (
    <ErrorBoundary>
      <div
        className="tax-calculator-widget min-h-0 flex flex-col"
        style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
      >
        <DynamicFlowEngine
          country={country}
          taxYear={taxYear}
          onComplete={handleComplete}
          region={region}
        />
      </div>
    </ErrorBoundary>
  );

  return (
    <WidgetConfigProvider config={config}>
      <QueryClientProvider client={widgetQueryClient}>
        {posthogClient ? (
          <PostHogProvider client={posthogClient}>{content}</PostHogProvider>
        ) : (
          content
        )}
      </QueryClientProvider>
    </WidgetConfigProvider>
  );
};

export default WidgetApp;
